import CarotekColors from "@carotek/utils/Colors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Select, { SelectPresentationProps } from "@insite/mobius/Select/Select";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";

import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    pagination: getProductListDataViewProperty(state, "pagination"),
});

const mapDispatchToProps = {
    addProductFilters,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductListPageSize {
    container?: InjectableCss;
    selectBox?: SelectPresentationProps;
}

export const productListPageSizeStyle: ProductListPageSize = {
    container: {
        css: css`
            display: flex;
            align-items: center;
            margin-right: 25px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-right: 0px;
                    `,
                    css`
                        margin-right: 0px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    selectBox: {
        backgroundColor: "transparent",
        css: css`
            height: auto;
        `,
        cssOverrides: {
            formInputWrapper: css`
                margin-top: 0 !important;
                [class^="IconWrapper"] {
                    top: 0px;
                    right: 0px;
                    width: 28px;
                    height: 28px;

                    background-image: url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZlcnNpb249IjEuMSIgeD0iMTJweCIgeT0iMHB4IiB3aWR0aD0iMjRweCIgaGVpZ2h0PSIzcHgiIHZpZXdCb3g9IjAgMCA2IDMiIGVuYWJsZS1iYWNrZ3JvdW5kPSJuZXcgMCAwIDYgMyIgeG1sOnNwYWNlPSJwcmVzZXJ2ZSI+PHBvbHlnb24gcG9pbnRzPSI1Ljk5MiwwIDIuOTkyLDMgLTAuMDA4LDAgIi8+PC9zdmc+);
                    background-repeat: no-repeat;
                    background-position: center center;
                    svg {
                        display: none;
                    }
                }
            `,
            formField: css`
                flex-direction: row;
                align-items: center;
            `,
            inputSelect: css`
                border: 1px solid ${CarotekColors.grey1};
                width: auto;
                min-width: 53px;
                max-width: 175px;
                font-size: 16px;
                font-weight: 400;
                color: ${getColor("text.main")};
                height: 2.5em;
                padding-top: 0.5em;
                padding-bottom: 0.5em;
                font-size: 12px;

                :focus,
                :focus-within {
                    box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.25) !important;
                    border: 1px solid ${CarotekColors.grey7} !important;
                    background-color: ${CarotekColors.white} !important;
                }
            `,
        },
        labelProps: {
            weight: 400,
            color: "text.main",
            size: 12,
            fontFamily: "OpenSansItalic",
            css: css`
                padding-right: 5px;
            `,
        },
    },
};

const styles = productListPageSizeStyle;

const ProductListPageSize: FC<Props> = ({ id, pagination, addProductFilters }) => {
    if (!pagination) {
        return null;
    }

    const [pageSize, setPageSize] = useState(pagination?.pageSize);
    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        setPageSize(newPageSize);
        addProductFilters({ pageSize: newPageSize, page: 1 });

        const dataLayer = (window as any).dataLayer || [];
        dataLayer.push({
            event: "show_items_per_page",
            page_with_selection: newPageSize,
        });
    };

    return (
        <StyledWrapper {...styles.container}>
            {/* <Typography>{translate("Per Page:")}</Typography> */}
            <Select
                label={translate("Per Page:")}
                preventEllipsis={true}
                onChange={changeResultsPerPage}
                value={pageSize}
                {...styles.selectBox}
            >
                {pagination?.pageSizeOptions.map(item => (
                    <option key={item} value={item}>
                        {item}
                    </option>
                ))}
            </Select>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListPageSize),
    definition: {
        group: "Product List",
        displayName: "Product List Page Size",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
